
/**
 * Module dependencies.
 */

import { createAction } from 'redux-actions'
import isEmpty from 'is-empty'
import request from 'superagent'
import { navigate } from '@segment/deku-enroute'
import { error, success } from '@segment/notify'
import { workspaceUpdated } from 'analytics'
import { getWorkspace } from 'selectors'
import fetch from 'lib/fetch'
import { fetchSources, fetchSourceTrackEvents } from './sources'

/**
 * Types.
 */

export const REQUEST_WORKSPACES = 'REQUEST_WORKSPACES'
export const RECEIVE_WORKSPACES = 'RECEIVE_WORKSPACES'
export const REQUEST_WORKSPACE = 'REQUEST_WORKSPACE'
export const REQUEST_WORKSPACE_ERROR = 'REQUEST_WORKSPACE_ERROR'
export const RECEIVE_WORKSPACE = 'RECEIVE_WORKSPACE'
export const FETCH_WORKSPACES_ERROR = 'FETCH_WORKSPACES_ERROR'
export const CREATING_WORKSPACE = 'CREATING_WORKSPACE'
export const CREATED_WORKSPACE = 'CREATED_WORKSPACE'
export const CREATE_WORKSPACE_ERROR = 'CREATE_WORKSPACE_ERROR'
export const UPDATING_WORKSPACE_BY_SLUG = 'UPDATING_WORKSPACE_BY_SLUG'
export const UPDATE_WORKSPACE_BY_SLUG_ERROR = 'UPDATE_WORKSPACE_BY_SLUG_ERROR'
export const UPDATED_WORKSPACE_BY_SLUG = 'UPDATED_WORKSPACE_BY_SLUG'
export const DELETING_WORKSPACE_BY_SLUG = 'DELETING_WORKSPACE_BY_SLUG'
export const DELETE_WORKSPACE_BY_SLUG_ERROR = 'DELETE_WORKSPACE_BY_SLUG_ERROR'
export const REQUEST_WORKSPACE_TRACK_EVENTS = 'REQUEST_WORKSPACE_TRACK_EVENTS'
export const REQUEST_WORKSPACE_TRACK_EVENTS_ERROR = 'REQUEST_WORKSPACE_TRACK_EVENTS_ERROR'
export const RECEIVE_WORKSPACE_TRACK_EVENTS = 'RECEIVE_WORKSPACE_TRACK_EVENTS'

/**
 * Actions.
 */

const requestWorkspaces = createAction(REQUEST_WORKSPACES)
const receiveWorkspaces = createAction(RECEIVE_WORKSPACES)
const fetchWorkspacesError = createAction(FETCH_WORKSPACES_ERROR)
const creatingWorkspace = createAction(CREATING_WORKSPACE)
const createdWorkspace = createAction(CREATED_WORKSPACE)
const createWorkspaceError = createAction(CREATE_WORKSPACE_ERROR)
const updatingWorkspaceBySlug = createAction(UPDATING_WORKSPACE_BY_SLUG)
const updateWorkspaceBySlugError = createAction(UPDATE_WORKSPACE_BY_SLUG_ERROR)
const updatedWorkspaceBySlug = createAction(UPDATED_WORKSPACE_BY_SLUG)
const deletingWorkspaceBySlug = createAction(DELETING_WORKSPACE_BY_SLUG)
const deleteWorkspaceBySlugError = createAction(DELETE_WORKSPACE_BY_SLUG_ERROR)

/**
 * Fetch all workspaces if needed.
 */

export function fetchWorkspacesIfNeeded () {
  return function (dispatch, getState) {
    const state = getState()
    if (shouldFetchWorkspaces(state)) {
      return dispatch(fetchWorkspaces())
    }
  }
}

export const fetchWorkspace = workspaceSlug => ({
  types: {
    request: REQUEST_WORKSPACE,
    receive: RECEIVE_WORKSPACE,
    failure: REQUEST_WORKSPACE_ERROR
  },
  payload: { workspaceSlug },
  shouldCallAPI: state => !getWorkspace(state, workspaceSlug),
  callAPI: () => fetch(`/api/workspaces/${workspaceSlug}`).then(res => {
    if (!res.ok) {
      const error = new Error(`Unable to fetch workspace "${workspaceSlug}"`)
      error.code = res.status
      throw error
    }
    return res.json()
  })
})

/**
 * Create a new workspace with `obj`.
 */

export function createWorkspace ({ data }) {
  return function (dispatch) {
    dispatch(creatingWorkspace({ data }))

    fetch('/api/workspaces', {
      method: 'POST',
      body: JSON.stringify(data)
    })
    .then(res => {
      if (!res.ok) {
        throw new Error('Failed to create workspace')
      }

      return res.json()
    }).then(data => {
      dispatch(createdWorkspace({ data }))
      navigate('/workspaces')
    }).catch(error => {
      dispatch(createWorkspaceError({ data: data.slug, error }))
    })
  }
}

/**
 * Fetch all workspaces.
 */

function fetchWorkspaces () {
  return function (dispatch) {
    dispatch(requestWorkspaces())
    request
    .get('/api/workspaces')
    .end(function (err, res) {
      if (err || !res.ok) {
        const error = err || new Error('Unable to fetch workspaces')
        dispatch(fetchWorkspacesError({ error }))
      } else {
        dispatch(receiveWorkspaces({ data: res.body }))
      }
    })
  }
}

/**
 * Should we fetch workspaces?
 */

function shouldFetchWorkspaces (state) {
  const { isRequesting, data, didInvalidate } = state.workspacesBySlug
  if (isRequesting) return false
  if (isEmpty(data)) return true
  return didInvalidate
}

export function updateWorkspaceBySlug ({ slug, data }) {
  return function (dispatch) {
    dispatch(updatingWorkspaceBySlug({ slug }))

    fetch(`/api/workspaces/${slug}`, {
      method: 'PATCH',
      body: JSON.stringify(data)
    })
    .then(res => {
      res.json().then(data => onWorkspaceUpdate(res, data, slug, dispatch))
    })
    .catch(err => {
      dispatch(updateWorkspaceBySlugError({ slug, error: err }))
    })
  }
}

function onWorkspaceUpdate (res, data, slug, dispatch) {
  if (!res.ok) {
    error('Unable to update workspace')
    dispatch(updateWorkspaceBySlugError({ slug }))
    return
  }

  success('Updated workspace')
  workspaceUpdated(data)

  if (data.slug !== slug) {
    window.location = `/${data.slug}/settings`
  } else {
    dispatch(updatedWorkspaceBySlug({ slug, data }))
  }
}

export function deleteWorkspaceBySlug (slug) {
  return function (dispatch) {
    dispatch(deletingWorkspaceBySlug({ slug }))

    fetch(`/api/workspaces/${slug}`, {
      method: 'DELETE'
    })
    .then(res => {
      if (res.ok) {
        success('Deleted workspace')
        window.location = '/workspaces'
      } else {
        error('Unable to delete workspace')
        dispatch(deleteWorkspaceBySlugError({ slug }))
      }
    })
    .catch(_err => {
      error('Unable to delete workspace')
      dispatch(deleteWorkspaceBySlugError({ slug }))
    })
  }
}

export const fetchWorkspaceTrackEvents = workspaceSlug => dispatch => {
  dispatch(fetchSources(workspaceSlug))
  .then(res => {
    const sources = res.payload.response
    const promises = []
    for (let i = 0; i < sources.length; i++) {
      promises.push(dispatch(fetchSourceTrackEvents({
        workspaceSlug,
        sourceSlug: sources[i].slug
      })))
    }
    return Promise.all(promises)
  })
}



// WEBPACK FOOTER //
// ./actions/workspaces.js